import { useLazyQuery } from "@apollo/client";
import { IonText } from "@ionic/react";
import { addDays } from "date-fns";
import { MutableRefObject, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PieChart } from "react-minimal-pie-chart";

import styles from "@components/expert/ExpertDashboardOverview.module.css";
import HandleQuery from "@components/HandleQuery";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToExpertTasks from "@hooks/subscriptions/useSubscribeToExpertTasks";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useSubscribeToMessages from "@hooks/subscriptions/useSubscribeToMessages";
import useMyExpert from "@hooks/useMyExpert";
import { GetExpertSummaryDocument, GetExpertSummaryQuery, GetExpertSummaryQueryVariables } from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";
import { dateToIso8601 } from "@utils/timeUtils";

type Props = {
  expertId: string;
  messagesRef?: MutableRefObject<HTMLDivElement | null>;
};

const ExpertDashboardOverview = ({ expertId, messagesRef }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const [getExpertSummary, getExpertSummaryQuery] = useLazyQuery<GetExpertSummaryQuery, GetExpertSummaryQueryVariables>(
    GetExpertSummaryDocument
  );

  useEffect(() => {
    if (expertId) {
      getExpertSummary({
        variables: {
          expertId
        }
      });
    }
  }, [getExpertSummary, expertId]);

  useSubscribeToExpertTasks(expertId ?? "", () => {
    getExpertSummaryQuery.refetch();
  });

  useSubscribeToMessages(() => {
    getExpertSummaryQuery.refetch();
  });

  useSubscribeToJourneys(() => {
    getExpertSummaryQuery.refetch();
  });

  const handleMessagesSelect = () => {
    messagesRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const today = dateToIso8601(new Date(), intl).split("T")[0];
  const tomorrow = dateToIso8601(addDays(new Date(), 1), intl).split("T")[0];

  const taskPercentages = useMemo(() => {
    if (getExpertSummaryQuery.data?.expertSummary) {
      const total =
        getExpertSummaryQuery.data.expertSummary.tasksOverdue +
        getExpertSummaryQuery.data.expertSummary.tasksToday +
        getExpertSummaryQuery.data.expertSummary.tasksUpcoming;

      return {
        overduePercent: (getExpertSummaryQuery.data.expertSummary.tasksOverdue / total) * 100,
        todayPercent: (getExpertSummaryQuery.data.expertSummary.tasksToday / total) * 100,
        totalCount: total,
        upcomingPercent: (getExpertSummaryQuery.data.expertSummary.tasksUpcoming / total) * 100
      };
    }
  }, [getExpertSummaryQuery.data?.expertSummary]);

  return (
    <HandleQuery query={getExpertSummaryQuery}>
      {summaryData => (
        <div className={styles.boxGroup}>
          <div className={styles.boxShadow}>
            <ButtonLink expand onClick={handleMessagesSelect}>
              <div className={`${styles.square} ${summaryData.expertSummary.unreadMessages === 0 ? styles.empty : ""}`}>
                <img
                  alt={intl.formatMessage({ id: "accessibility.altTags.mail" })}
                  className={styles.mailImage}
                  src={imageUrl("mail")}
                />
                {summaryData.expertSummary.unreadMessages > 0 && (
                  <div className={styles.badge}>{summaryData.expertSummary.unreadMessages}</div>
                )}
                <div className={styles.center}>
                  <FormattedMessage
                    id={`pages.expert.dashboard.overview.${
                      summaryData.expertSummary.unreadMessages === 0 ? "noMessages" : "newMessages"
                    }`}
                    values={{ count: summaryData.expertSummary.unreadMessages }}
                  />
                </div>
              </div>
            </ButtonLink>
          </div>
          <div className={styles.boxShadow}>
            <InternalLink
              expand
              linkEventCreateVariables={{}}
              stylized={false}
              to={`/expert/consults?startTime=${today}&endTime=${tomorrow}&expertId=${expertId}`}
            >
              <div className={`${styles.square} ${summaryData.expertSummary.consultsToday === 0 ? styles.empty : ""}`}>
                <img
                  alt={intl.formatMessage({ id: "accessibility.altTags.calendar" })}
                  className={styles.calendarImage}
                  src={imageUrl("calendar")}
                />
                {summaryData.expertSummary.consultsToday > 0 && (
                  <div className={`${styles.badge} ${styles.consultBadge}`}>
                    {summaryData.expertSummary.consultsToday}
                  </div>
                )}
                <div className={styles.center}>
                  <FormattedMessage
                    id={`pages.expert.dashboard.overview.${
                      summaryData.expertSummary.consultsToday === 0 ? "noConsultsToday" : "consultsToday"
                    }`}
                    values={{ count: summaryData.expertSummary.consultsToday }}
                  />
                </div>
              </div>
            </InternalLink>
          </div>
          <div className={styles.boxShadow}>
            <InternalLink
              expand
              linkEventCreateVariables={{}}
              stylized={false}
              to={`/expert/tasks?expertId=${expertId}`}
            >
              <div className={`${styles.square} ${taskPercentages?.totalCount === 0 ? styles.empty : ""}`}>
                {taskPercentages && (
                  <div>
                    <div className={styles.bar}>
                      <div
                        className={styles.barItemOverdue}
                        style={{ width: `${taskPercentages.overduePercent.toString()}%` }}
                      />
                      <div
                        className={styles.barItemToday}
                        style={{ width: `${taskPercentages.todayPercent.toString()}%` }}
                      />
                      <div
                        className={styles.barItemUpcoming}
                        style={{ width: `${taskPercentages.upcomingPercent.toString()}%` }}
                      />
                    </div>
                    <div className={`${styles.barKey} ion-margin-top`}>
                      <div className={styles.barKeyItem}>
                        <div className={styles.barKeyColorRed} />
                        <IonText>
                          <FormattedMessage
                            id="pages.expert.dashboard.overview.overdue"
                            values={{ count: summaryData.expertSummary.tasksOverdue }}
                          />
                        </IonText>
                      </div>
                      <div className={styles.barKeyItem}>
                        <div className={styles.barKeyColorTeal} />
                        <IonText>
                          <FormattedMessage
                            id="pages.expert.dashboard.overview.today"
                            values={{ count: summaryData.expertSummary.tasksToday }}
                          />
                        </IonText>
                      </div>
                      <div className={styles.barKeyItem}>
                        <div className={styles.barKeyColorPurple} />
                        <IonText>
                          <FormattedMessage
                            id="pages.expert.dashboard.overview.upcoming"
                            values={{ count: summaryData.expertSummary.tasksUpcoming }}
                          />
                        </IonText>
                      </div>
                    </div>
                  </div>
                )}
                <div className={styles.center}>
                  <FormattedMessage
                    id="pages.expert.dashboard.overview.totalTasks"
                    values={{
                      count:
                        summaryData.expertSummary.tasksUpcoming +
                        summaryData.expertSummary.tasksOverdue +
                        summaryData.expertSummary.tasksToday
                    }}
                  />
                </div>
              </div>
            </InternalLink>
          </div>
          <div className={styles.boxShadow}>
            <InternalLink
              expand
              linkEventCreateVariables={{}}
              stylized={false}
              to={myExpert?.id === expertId ? "/expert/journeys" : `/expert/manage-experts/${expertId}/journeys`}
            >
              <div
                className={`${styles.square} ${styles.pieChartSquare} ${
                  summaryData.expertSummary.clientsActive + summaryData.expertSummary.clientsPreConsult === 0
                    ? styles.empty
                    : ""
                }`}
              >
                <div className="full-width">
                  <div className={styles.pieChart}>
                    <PieChart
                      data={[
                        { color: "#6ab7d0", value: summaryData.expertSummary.clientsActive },
                        { color: "#877ff7", value: summaryData.expertSummary.clientsPreConsult }
                      ]}
                      lineWidth={20}
                    />
                    <div className={styles.totalClients}>
                      <div>
                        <IonText className={styles.clientNumber}>
                          {summaryData.expertSummary.clientsActive + summaryData.expertSummary.clientsPreConsult}
                        </IonText>
                      </div>
                      <IonText className={styles.clients}>
                        <FormattedMessage id="pages.expert.dashboard.overview.clients" />
                      </IonText>
                    </div>
                  </div>
                  <div className={styles.barKey}>
                    <div className={styles.barKeyItem}>
                      <div className={styles.barKeyColorTeal} />
                      <IonText>
                        <FormattedMessage
                          id="pages.expert.dashboard.overview.active"
                          values={{ count: summaryData.expertSummary.clientsActive }}
                        />
                      </IonText>
                    </div>
                    <div className={styles.barKeyItem}>
                      <div className={styles.barKeyColorPurple} />
                      <IonText>
                        <FormattedMessage
                          id="pages.expert.dashboard.overview.preConsult"
                          values={{ count: summaryData.expertSummary.clientsPreConsult }}
                        />
                      </IonText>
                    </div>
                  </div>
                </div>
                <div className={styles.center}>
                  <FormattedMessage
                    id="pages.expert.dashboard.overview.totalClients"
                    values={{
                      count: summaryData.expertSummary.clientsActive + summaryData.expertSummary.clientsPreConsult
                    }}
                  />
                </div>
              </div>
            </InternalLink>
          </div>
        </div>
      )}
    </HandleQuery>
  );
};

export default ExpertDashboardOverview;
